import React from "react";

interface LogoProps {
  className?: string;
  /** When true, only show the icon (useful for collapsed sidebars or compact spaces). */
  iconOnly?: boolean;
  /** Render with inverted (white) colors for dark backgrounds. */
  inverted?: boolean;
  /** Optional variant for typography: 'MíA' (recommended, highlights IA) or 'Mía' */
  variant?: "MíA" | "Mía";
}

/**
 * Custom SVG Icon replicating the stylized "MíA" ligature mark:
 * An elegant loop forming an 'a' with an inner dot forming the 'i' -> 'ia'
 */
export const MiaMarkIcon: React.FC<{ size?: number; className?: string }> = ({
  size = 38,
  className = "",
}) => {
  return (
    <div
      className={`relative shrink-0 select-none ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-md transition-transform duration-300 hover:scale-105"
      >
        <defs>
          <linearGradient id="mia-squircle-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#280f5c" />
            <stop offset="50%" stopColor="#1e1854" />
            <stop offset="100%" stopColor="#1a2e7b" />
          </linearGradient>
          <filter id="soft-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.25" />
          </filter>
        </defs>

        {/* Squircle Background Container */}
        <rect
          width="100"
          height="100"
          rx="26"
          fill="url(#mia-squircle-gradient)"
        />

        {/* Continuous Fluid Loop 'a/A' */}
        <path
          d="M 54 62 C 46 71 36 73 31 66 C 24 57 23 42 30 28 C 36 17 46 14 53 14 C 64 14 74 21 79 34 C 84 48 84 62 82 73"
          stroke="#FFFFFF"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#soft-glow)"
        />

        {/* Solid Circular Dot ('i' accent & anchor) */}
        <circle cx="65" cy="74" r="6.8" fill="#FFFFFF" filter="url(#soft-glow)" />
      </svg>
    </div>
  );
};

const Logo = ({
  className = "",
  iconOnly = false,
  inverted = false,
  variant = "MíA",
}: LogoProps) => {
  const textColor = inverted ? "text-white" : "text-slate-900 dark:text-white";

  return (
    <div className={`inline-flex items-center gap-2.5 select-none ${className}`}>
      {/* Brand Isotype Symbol */}
      <MiaMarkIcon size={38} />

      {/* Brand Wordmark Typography */}
      {!iconOnly && (
        <div className="flex flex-col leading-none">
          <div className="flex items-baseline tracking-tight">
            {variant === "MíA" ? (
              <>
                <span className={`font-black text-2xl ${textColor} tracking-tight`}>
                  Mí
                </span>
                <span className="font-black text-2xl text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 dark:from-blue-400 dark:to-indigo-300 ml-[1px]">
                  A
                </span>
              </>
            ) : (
              <span className={`font-black text-2xl ${textColor} tracking-tight`}>
                Mía
              </span>
            )}
          </div>
          <span className="text-[8.5px] font-extrabold tracking-[0.22em] uppercase text-blue-600 dark:text-blue-400 mt-1">
            Asistente Legal IA
          </span>
        </div>
      )}
    </div>
  );
};

export default Logo;
